<template>
  <div class="eligibility-form">
    <div class="form-section">
      <FormField :labelText="serviceConnectionLabel">
        <ul class="radio-group large">
          <li v-for="option in refData.serviceConnectionOptions" :key="option.display">
            <input
              type="radio"
              :id="option.returnValue"
              :value="option.display"
              name="serviceConnection"
              v-model="formResponse.serviceConnection"
              @change="updatePartnerNameField"
              :disabled="disableEligibilityFields"
            />
            <label :for="option.returnValue">{{ option.display }}</label>
          </li>
        </ul>
      </FormField>
    </div>

    <div class="form-section" v-if="formResponse.serviceConnection.toLowerCase() === 'partner'" :key="`partner-name${disableEligibilityFields ? '-disable' : ''}`">
      <!-- partner name input -->
      <template v-if="disableEligibilityFields">
        <FormField id="partner-name" :labelText="`${Eligibility.EX_SERVING.includes(eligibilityType) ? 'Veteran' : 'Partner'}'s name`">
          <p class="disabled-input">{{ returnWhiteSpaceIfEmptyStr(formResponse.serviceConnectionName) }}</p>
        </FormField>
      </template>
      <template v-else>
        <FormField
          :class="{ error: v$.formResponse.serviceConnectionName.$error, dirty: v$.formResponse.serviceConnectionName.$dirty }"
          id="partner-name"
          :labelText="`${Eligibility.EX_SERVING.includes(eligibilityType) ? 'Veteran' : 'Partner'}'s name`"
          placeholder="Please enter full name"
          type="text"
          v-model="v$.formResponse.serviceConnectionName.$model"
          :error="v$.formResponse.serviceConnectionName.$error"
          :errorMessage="getErrorMessage"
        />
      </template>
    </div>

    <div class="form-section">
      <!-- form inputs for each eligibility types -->
      <component :is="getComponent()" v-bind="currentProperties" ref="eligibilityType" v-model="formResponse.subFormData" @updated="updateSubFormData" />
    </div>

    <!-- If there is no available declaration label, hide this field -->
    <div class="form-section" v-if="showDeclaration">
      <FormField>
        <input
          id="declaration"
          type="checkbox"
          value="true"
          v-model="formResponse.declaration"
          name="declaration"
          :disabled="disableEligibilityFields"
          @keyup.enter.space="formResponse.declaration = !formResponse.declaration"
          @change="updateDeclarationDates(formResponse.declaration)"
        />
        <label for="declaration">
          {{ getDeclarationLabel(this.declarationLabelType) }}
          <span class="required-input">*</span>
        </label>
      </FormField>
    </div>
  </div>
</template>

<script>
import { required, maxLength, requiredIf } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import { FormField } from '@/components';
import { JOIN_NOW_NAME_VALIDATOR } from '@general/js/validators';
import { CurrentlyServing, DEFAULT_VETERAN_INFO, ExServing, Supporting, WorkConnection } from '@general/js/templates/JoinNow/JoinNowSteps/EligibilityTypes';
import { getDeclarationLabel, isInvalid } from '@general/js/utils';
import { formatDate, getPastOrLaterDateFromInitialDate } from '@general/js/utils/dateTimeUtils';
import { AcceptableThisEligibilityType, EligibilityTypes } from '@/enums';
import { returnWhiteSpaceIfEmptyStr } from '@/utils';

export default {
  setup() { return { v$: useVuelidate() }; },
  name: 'Eligibility-form',

  components: {
    FormField,
    CurrentlyServing,
    ExServing,
    Supporting,
    WorkConnection,
  },

  props: {
    refData: {
      type: Object,
      required: true,
    },

    /**
     * format of the object must follow form response data object from Eligibility of Join Online
     */
    formData: {
      type: Object,
      required: true,
    },

    /**
     * this value have to be one of following:
     * - "ADF"
     * - "Currently serving"
     * - "ExServing"
     * - "Ex-serving"
     * - "WorkConnection"
     * - "Supporting"
     */
    eligibilityType: {
      type: String,
      required: true,
    },

    fileRequired: {
      type: Boolean,
      default: false,
    },

    /**
     * contact ID (GUID) or member number (policy number).
     * contact ID would be used for join online upload SERCAT file.
     * member number would be used for update member details upload SERCAT file.
     */
    contactIdOrMemberNumber: {
      type: String,
      required: true,
    },
    coverStartDate: {
      type: Date,
    },

    /**
     * The current eligibility of policy holder for prefilling in Change Cover
     */
    currentEligibility: {
      type: String,
      default: null,
    },

    /**
     * Check if there are changes in partner information in Change Cover
     */
    partnerInfoChanged: {
      type: Boolean,
      default: false,
    },

    /**
     * Checks whether form is for Change Cover
     */
    isChangeCover: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      defaultData: {},
      defaultSubFormTemplate: {},
      initialData: {},
      formLabels: {
        serviceConnectionLabel: '',
        declarationLabel: '',
      },
      formResponse: {
        serviceConnection: '',
        serviceConnectionName: '',
        declaration: false,
        declarationDate: null,
        declarationExpiryDate: null,
        subFormData: {},
      },
      Eligibility: EligibilityTypes,
      AcceptableThisEligibilityType,
      subFormIsInvalid: true,
      declarationLabelType: null,
    };
  },

  mounted() {
    const initialSubFormData = this.setUpSubFormTemplate();

    this.defaultData = Object.freeze({
      ...this.formResponse,
      subFormData: this.defaultSubFormTemplate,
    });

    this.formResponse = {
      ...this.formResponse,
      ...this.formData,
    };

    this.initialData = Object.freeze({
      eligibilityType: this.eligibilityType,
      serviceConnection: this.formResponse.serviceConnection,
      serviceConnectionName: this.formResponse.serviceConnectionName,
      formResponse: this.formResponse,
      subFormData: initialSubFormData,
    });

    // disableEligibilityFields is based off if this form is Change Cover
    // If declaration field is not disabled, reset to false
    if (this.isChangeCover) {
      this.formResponse.declaration = this.disableEligibilityFields;
    }
  },

  watch: {
    v$: {
      handler(newData) {
        this.$nextTick(() => {
          this.$emit('responseUpdated', this.formResponse, newData.$invalid);
        });
      },

      deep: true,
    },

    eligibilityType(newVal) {
      if (newVal === this.initialData.eligibilityType) {
        this.formResponse.serviceConnection = this.initialData.serviceConnection;
        this.formResponse.serviceConnectionName = this.initialData.serviceConnectionName;
      } else {
        this.formResponse.serviceConnection = '';
        this.formResponse.serviceConnectionName = '';
      }
    },

    'formResponse.serviceConnection': function (newVal) {
      if (this.isChangeCover && this.noEligibilityChange && this.initialData.serviceConnection !== '') {
        if (newVal === this.initialData.serviceConnection) {
          this.formResponse = { ...this.initialData.formResponse, subFormData: this.initialData.subFormData, serviceConnection: this.formResponse.serviceConnection };
        } else {
          this.formResponse = { ...this.defaultData, serviceConnection: this.formResponse.serviceConnection, subFormData: this.formResponse.subFormData };
        }
      }
    },

    'formData.declaration': {
      handler(newVal) {
        this.formResponse.declaration = newVal;
      },
    },
  },

  validations: {
    formResponse: {
      serviceConnection: {
        required,
      },
      serviceConnectionName: {
        required: requiredIf(function () {
          return this.formResponse.serviceConnection === 'Partner';
        }),
        JOIN_NOW_NAME_VALIDATOR,
        maxLength: maxLength(480),
      },
      declaration: {
        required,
        isSelected: (value) => {
          return value;
        },
      },
    },
    subFormIsInvalid: {
      isInvalid,
    },
  },

  computed: {
    showDeclaration() {
      if (
        [AcceptableThisEligibilityType.SERVING, AcceptableThisEligibilityType.EX_SERVING].includes(this.thisEligibilityType) ||
        ([AcceptableThisEligibilityType.RELATED, AcceptableThisEligibilityType.WORK_CONNECTION].includes(this.thisEligibilityType) &&
          this.formResponse.subFormData.connectionType !== '')
      ) {
        return true;
      }

      return false;
    },

    thisEligibilityType() {
      if (this.Eligibility.SERVING.includes(this.eligibilityType)) {
        return AcceptableThisEligibilityType.SERVING;
      }

      if (this.Eligibility.EX_SERVING.includes(this.eligibilityType)) {
        return AcceptableThisEligibilityType.EX_SERVING;
      }

      if (this.Eligibility.RELATED.includes(this.eligibilityType)) {
        return AcceptableThisEligibilityType.RELATED;
      }

      if (this.Eligibility.WORK_CONNECTION.includes(this.eligibilityType)) {
        return AcceptableThisEligibilityType.WORK_CONNECTION;
      }

      return '';
    },

    currentProperties() {
      const obj = {
        referenceData: this.refData,
        subFormData: this.setUpSubFormTemplate(),
        noEligibilityChange: this.noEligibilityChange,
        disableEligibilityFields: this.disableEligibilityFields,
        initialData: { serviceConnection: this.initialData.serviceConnection, subFormData: this.initialData.subFormData },
        serviceConnection: this.formResponse.serviceConnection,
        defaultSubFormTemplate: this.defaultSubFormTemplate,
        isChangeCover: this.isChangeCover,
      };

      if (this.Eligibility.SERVING.includes(this.eligibilityType)) {
        obj.contactId = this.contactIdOrMemberNumber;
        obj.fileRequired = this.fileRequired;
      }

      return obj;
    },

    serviceConnectionLabel() {
      switch (this.thisEligibilityType) {
        case AcceptableThisEligibilityType.SERVING:
          return 'Who is currently serving?';

        case AcceptableThisEligibilityType.EX_SERVING:
          return 'Who served?';

        case AcceptableThisEligibilityType.RELATED:
          return 'Who is related to the person who serves/served?';

        case AcceptableThisEligibilityType.WORK_CONNECTION:
          return 'Who had/has a work connection to the Defence or a Defence –related government department?';

        default:
          return '';
      }
    },

    getErrorMessage() {
      if (this.v$.formResponse.serviceConnectionName.$error && this.v$.formResponse.serviceConnectionName.JOIN_NOW_NAME_VALIDATOR.$invalid) {
        return "We can't accept that input - please remove numbers, symbols and/or consecutive spaces.";
      }

      if (this.v$.formResponse.serviceConnectionName.$error && this.v$.formResponse.serviceConnectionName.maxLength.$invalid) {
        return 'You have exceeded the maximum amount of characters.';
      }

      return `Please include a response for ${this.Eligibility.EX_SERVING.includes(this.currentEligibility) ? 'Veteran' : 'Partner'}'s name.`;
    },

    // Checks if the eligibility saved in CRM is the same as selected in Change cover quote
    noEligibilityChange() {
      return this.currentEligibility === this.eligibilityType;
    },

    // Checks if the eligibility saved in CRM is the same as selected in Change cover quote and if partner has no changes
    disableEligibilityFields() {
      return this.noEligibilityChange && !this.partnerInfoChanged;
    },
  },

  methods: {
    setUpSubFormTemplate() {
      switch (this.thisEligibilityType) {
        case AcceptableThisEligibilityType.SERVING:
          this.defaultSubFormTemplate = { enlistmentDate: '', sercatStatus: '', sercatFileNames: [], veteranInfo: { ...DEFAULT_VETERAN_INFO } };
          break;

        case AcceptableThisEligibilityType.EX_SERVING:
          this.defaultSubFormTemplate = { enlistmentDate: '', dischargeDate: '', veteranInfo: { ...DEFAULT_VETERAN_INFO } };
          break;

        case AcceptableThisEligibilityType.WORK_CONNECTION:
          this.defaultSubFormTemplate = { relationName: '', connectionName: '', connectionType: '' };
          break;

        case AcceptableThisEligibilityType.RELATED:
          this.defaultSubFormTemplate = { relationName: '', relationship: '', connectionName: '', connectionType: '', veteranInfo: { ...DEFAULT_VETERAN_INFO } };
          break;

        default:
          break;
      }

      const subFormTemplate = {
        ...this.defaultSubFormTemplate,
        ...this.formData.subFormData,
      };

      return subFormTemplate;
    },

    updateSubFormData(data) {
      this.formResponse.subFormData = JSON.parse(JSON.stringify(data.subFormData));
      this.subFormIsInvalid = data.isInvalid;
    },

    updatePartnerNameField() {
      this.formResponse.serviceConnectionName = '';
    },

    updateDeclarationDates(declarationStatus) {
      if (declarationStatus) {
        this.formResponse.declarationDate = formatDate(this.coverStartDate);
        const declarationExpiryPeriod = 1;

        // If eligibility is 'Currently serving' get the declaration expiry date (1 year from cover start date)
        if (this.Eligibility.SERVING.includes(this.eligibilityType)) {
          this.formResponse.declarationExpiryDate = getPastOrLaterDateFromInitialDate(this.formResponse.declarationDate, declarationExpiryPeriod);
        } else {
          this.formResponse.declarationExpiryDate = null;
        }
      } else {
        this.formResponse.declarationDate = null;
        this.formResponse.declarationExpiryDate = null;
      }
    },

    getComponent() {
      // Return name of component for the page
      switch (this.thisEligibilityType) {
        case AcceptableThisEligibilityType.SERVING:
          this.declarationLabelType = this.eligibilityType;
          return 'CurrentlyServing';

        case AcceptableThisEligibilityType.EX_SERVING:
          this.declarationLabelType = this.eligibilityType;
          return 'ExServing';

        case AcceptableThisEligibilityType.WORK_CONNECTION:
          this.declarationLabelType = this.formResponse.subFormData.connectionType;
          return 'WorkConnection';

        case AcceptableThisEligibilityType.RELATED:
          this.declarationLabelType = this.formResponse.subFormData.connectionType;
          return 'Supporting';

        default:
          return '';
      }
    },

    getDeclarationLabel,
    returnWhiteSpaceIfEmptyStr,
  },
};
</script>

<style lang="scss" scoped></style>
